import { NECESSARY } from "constants/categories";
import { _WINDOW_ } from "constants/dom";
import { FALSE, NULL, TRUE, UNDEFINED } from "constants/primitives";
import { EMBED_ELEMENTS_PREFIX, JS_API_OBJECT_NAME } from "core/config";
import classNames from "helpers/classNames";
import useAppState from "hooks/useAppState";
import useTranslation from "hooks/useTranslation";
import { type FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  ACCESSIBILITY_BLOCK_LEAVING_BLOCK,
  ACCESSIBILITY_BLOCK_LEAVING_DISMISS,
} from "types/accessibility";
import { IS_LEGACY_MODE } from "constants/modes";

const cx = classNames();

const RootContainer: FunctionComponent<IRootContainerProps> = ({
  isDialog = FALSE,
  children,
  rootClassName = "",
  forceHidden = FALSE,
}) => {
  const { t, textDirection, userLang } = useTranslation();
  const { widgetConfig, shouldBannerBeVisible } = useAppState();
  const [shouldBeOpen, setShouldBeOpen] = useState(FALSE);
  const dialogRef = useRef<HTMLDialogElement | null>(NULL);
  const rootDivRef = useRef<HTMLDivElement | null>(NULL);
  // Variable to store if user has used mouse to interact with the site, in that case disable trap.
  const hasUsedMouse = useRef(FALSE);
  const focusOutTimeout = useRef<ReturnType<typeof setTimeout> | null>(NULL);

  // Handle initial focus when the dialog is opened
  useEffect(() => {
    if (
      dialogRef.current &&
      isDialog &&
      !shouldBeOpen &&
      widgetConfig.focusBannerFirst
    ) {
      // Check if banner is already set as opened, this might happen when component re-render. In that case we don't want to reopen it via API to not change user focus position
      if (!shouldBeOpen) {
        dialogRef.current.focus({ preventScroll: TRUE });

        setShouldBeOpen(TRUE);
      }
    }
  }, [
    dialogRef,
    isDialog,
    widgetConfig.backdropEnabled,
    widgetConfig.focusBannerFirst,
    shouldBeOpen,
  ]);

  // Handle trapping user focus inside the banner or dismissing it on leave
  useEffect(() => {
    // Check if user swapped focus out of the banner. In case option to prevent going out of banner is enabled.
    const isInsideBanner = (target: any) => {
      if (
        target &&
        target.closest(`[data-id='${EMBED_ELEMENTS_PREFIX}-root']`)
      ) {
        return TRUE;
      } else {
        return FALSE;
      }
    };

    /**
     *
     * @param e - event
     * @param actionOut - action to perform when focus is out of the banner
     * @returns
     */
    const focusOutHandler = (
      e: any,
      actionOut: (event: typeof e, newTarget: any, oldTarget: any) => void
    ) => {
      // If user has already used mouse, we don't want to block focus out of the banner.
      if (hasUsedMouse.current) {
        return;
      }

      const newTarget = e.relatedTarget;
      const oldTarget = e.target;

      // If its not inside, check if it has been inside before
      if (!isInsideBanner(newTarget) && isInsideBanner(oldTarget)) {
        console.log("Action out triggered");
        // Trigger at
        actionOut(e, newTarget, oldTarget);

        return false;
      }
    };

    const blockLeavingSetting = widgetConfig.accessibilityBlockLeaving;

    // Don't continue if both options are disabled, we might check if option is just disabled. But we check for settings directly for future if there will be some more options
    if (
      blockLeavingSetting !== ACCESSIBILITY_BLOCK_LEAVING_BLOCK &&
      blockLeavingSetting !== ACCESSIBILITY_BLOCK_LEAVING_DISMISS
    ) {
      return;
    }

    /**
     * We need to handle click event to check if user has used mouse to interact with the site. We want to affect only keyboard users
     */
    const eventClickHandler = (): void => {
      // Clear timeout to not automatically dismiss banner
      if (focusOutTimeout.current) {
        clearTimeout(focusOutTimeout.current);
      }

      if (!hasUsedMouse.current) {
        hasUsedMouse.current = TRUE;
      }
    };

    // We create helper function in order to be able to remove event listener later
    const eventOutHelper = (e: FocusEvent): void => {
      if (blockLeavingSetting === ACCESSIBILITY_BLOCK_LEAVING_BLOCK) {
        focusOutHandler(e, (e, newTarget, oldTarget) => {
          if (oldTarget) {
            oldTarget.focus();
          }
        });
      }

      if (blockLeavingSetting === ACCESSIBILITY_BLOCK_LEAVING_DISMISS) {
        focusOutHandler(e, () => {
          console.log("set timeout");
          focusOutTimeout.current = setTimeout(() => {
            console.log("ACCEPT ");
            _WINDOW_[JS_API_OBJECT_NAME].acceptCategory(NECESSARY);
          }, 30000); // 30seconds;
        });
      }
    };

    // Handle disabling timeout when user focuses back on the banner
    const eventInHelper = (e: FocusEvent): void => {
      if (isInsideBanner(e.target) && focusOutTimeout.current) {
        clearTimeout(focusOutTimeout.current);
        focusOutTimeout.current = NULL;
      }
    };

    /**
     * Get all focusable elements within the banner
     * @returns Array of focusable elements
     */
    const getFocusableElements = (): HTMLElement[] => {
      const rootElement =
        dialogRef.current ||
        rootDivRef.current ||
        document.querySelector(
          `[data-id='${EMBED_ELEMENTS_PREFIX}-root']`
        );
      if (!rootElement) {
        return [];
      }

      // Selector for focusable elements
      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      const focusableElements = Array.from(
        rootElement.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((el) => {
        // Filter out elements that are not visible or have display: none
        const style = window.getComputedStyle(el);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          !el.hasAttribute('disabled')
        );
      });

      return focusableElements;
    };

    /**
     * Handle keyboard navigation to trap focus within the banner
     * @param e - Keyboard event
     */
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Only handle Tab key when block leaving is enabled and mouse hasn't been used
      if (
        blockLeavingSetting !== ACCESSIBILITY_BLOCK_LEAVING_BLOCK ||
        hasUsedMouse.current ||
        e.key !== 'Tab'
      ) {
        return;
      }

      // Check if the currently focused element is inside the banner
      const activeElement = document.activeElement as HTMLElement;
      if (!isInsideBanner(activeElement)) {
        return;
      }

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      // If Shift+Tab is pressed on the first focusable element, wrap to the last
      if (e.shiftKey && activeElement === firstFocusable) {
        e.preventDefault();
        e.stopPropagation();
        lastFocusable.focus();
        return;
      }

      // If Tab is pressed on the last focusable element, wrap to the first
      if (!e.shiftKey && activeElement === lastFocusable) {
        e.preventDefault();
        e.stopPropagation();
        firstFocusable.focus();
        return;
      }
    };

    window.addEventListener("click", eventClickHandler);
    window.addEventListener("focusout", eventOutHelper);
    window.addEventListener("focusin", eventInHelper);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("click", eventClickHandler);
      window.removeEventListener("focusout", eventOutHelper);
      window.removeEventListener("focusin", eventInHelper);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [widgetConfig.accessibilityBlockLeaving]);

  if (isDialog && !IS_LEGACY_MODE) {
    return (
      <dialog
        data-nosnippet // We add this to prevent Google from indexing the banner
        ref={dialogRef}
        data-id={`${EMBED_ELEMENTS_PREFIX}-root`}
        class={cx(
          `${EMBED_ELEMENTS_PREFIX}-root`,
          "notranslate",
          rootClassName,
          { "d-none": forceHidden }
        )}
        lang={userLang}
        dir={textDirection}
        translate={false}
        aria-label={t("modal.acc_title")}
        aria-modal={TRUE}
        role="dialog"
        open={forceHidden ? FALSE : shouldBeOpen && shouldBannerBeVisible}
        // We add preventDefault on cancel, to prevent browser from thinking that it was close, when in reality it hasn't been closed.
        onCancel={(event) => event.preventDefault()}
      >
        {children}
      </dialog>
    );
  }

  return (
    <div
      data-nosnippet // We add this to prevent Google from indexing the banner
      ref={rootDivRef}
      class={cx(`${EMBED_ELEMENTS_PREFIX}-root`, "notranslate", rootClassName, {
        "d-none": forceHidden,
      })}
      lang={userLang}
      dir={textDirection}
      translate={false}
      aria-label={t("modal.acc_title")}
      role={IS_LEGACY_MODE ? "dialog" : UNDEFINED}
      aria-modal={IS_LEGACY_MODE ? TRUE : UNDEFINED}
    >
      {children}
    </div>
  );
};

export interface IRootContainerProps {
  isDialog?: boolean;
  rootClassName?: string;
  forceHidden?: boolean;
  shouldBlockFocusOut?: boolean;
  shouldDismissOnLeave?: boolean;
}

export default RootContainer;
